import React, { ReactNode } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

export interface EditableColumn<T> {
  key: keyof T
  header: string
  type: 'text' | 'number' | 'select' | 'textarea' | 'readonly'
  width?: string
  selectOptions?: Array<{ value: string; label: string }>
  placeholder?: string
  allowBulkSet?: boolean
  className?: string
  customHeaderControl?: React.ReactNode
}

export interface EditableTableProps<T> {
  data: T[]
  columns: EditableColumn<T>[]
  onDataChange: (data: T[]) => void
  onBulkSetColumn?: (columnKey: keyof T, value: any) => void
  className?: string
  emptyMessage?: string
}

export function EditableTable<T extends Record<string, any>>({
  data,
  columns,
  onDataChange,
  onBulkSetColumn,
  className,
  emptyMessage = 'Inga poster att visa'
}: EditableTableProps<T>) {

  const handleCellChange = (rowIndex: number, columnKey: keyof T, value: any) => {
    const newData = [...data]
    newData[rowIndex] = { ...newData[rowIndex], [columnKey]: value }
    onDataChange(newData)
  }

  const handleBulkSet = (columnKey: keyof T, value: any) => {
    if (onBulkSetColumn) {
      onBulkSetColumn(columnKey, value)
    }
  }

  const renderCell = (row: T, column: EditableColumn<T>, rowIndex: number) => {
    const value = row[column.key]

    if (column.type === 'readonly') {
      return <span className="text-sm">{value}</span>
    }

    if (column.type === 'select') {
      return (
        <Combobox
          options={column.selectOptions || []}
          value={value || ''}
          onValueChange={(newValue) => handleCellChange(rowIndex, column.key, newValue)}
          placeholder={column.placeholder}
          searchPlaceholder="Sök..."
          className="min-w-[150px]"
        />
      )
    }

    if (column.type === 'textarea') {
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
          placeholder={column.placeholder}
          rows={1}
          className="min-w-[200px] min-h-[32px] resize-none"
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement
            target.style.height = 'auto'
            target.style.height = target.scrollHeight + 'px'
          }}
        />
      )
    }

    if (column.type === 'number') {
      return (
        <Input
          type="number"
          step="0.01"
          min="0"
          value={value || ''}
          onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value ? Number(e.target.value) : undefined)}
          placeholder={column.placeholder}
          className="min-w-[120px]"
        />
      )
    }

    // Default to text input
    return (
      <Input
        type="text"
        value={value || ''}
        onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
        placeholder={column.placeholder}
        className="min-w-[150px]"
      />
    )
  }

  const renderBulkSetControl = (column: EditableColumn<T>) => {
    if (!column.allowBulkSet || column.type === 'readonly') {
      return null
    }

    if (column.type === 'select') {
      return (
        <div className="flex gap-1">
          <Combobox
            options={column.selectOptions || []}
            value=""
            onValueChange={(value) => handleBulkSet(column.key, value)}
            placeholder="Sätt alla..."
            searchPlaceholder="Sök..."
            className="text-xs h-7 text-xs"
          />
        </div>
      )
    }

    if (column.type === 'textarea') {
      return (
        <div className="flex gap-1">
          <Textarea
            placeholder="Sätt alla..."
            className="text-xs min-h-[28px] resize-none"
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                const target = e.target as HTMLTextAreaElement
                if (target.value) {
                  handleBulkSet(column.key, target.value)
                  target.value = ''
                }
              }
            }}
            onBlur={(e) => {
              if (e.target.value) {
                handleBulkSet(column.key, e.target.value)
                e.target.value = ''
              }
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement
              target.style.height = 'auto'
              target.style.height = target.scrollHeight + 'px'
            }}
          />
        </div>
      )
    }

    return (
      <div className="flex gap-1">
        <Input
          type={column.type === 'number' ? 'number' : 'text'}
          placeholder="Sätt alla..."
          className="text-xs h-7"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const target = e.target as HTMLInputElement
              const value = column.type === 'number' && target.value
                ? Number(target.value)
                : target.value
              if (target.value) {
                handleBulkSet(column.key, value)
                target.value = ''
              }
            }
          }}
          onBlur={(e) => {
            const value = column.type === 'number' && e.target.value
              ? Number(e.target.value)
              : e.target.value
            if (e.target.value) {
              handleBulkSet(column.key, value)
              e.target.value = ''
            }
          }}
        />
      </div>
    )
  }

  return (
    <div className={cn('rounded-md border overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <Table className="min-w-full">
          <TableHeader className="sticky top-0 z-10">
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.key as string}
                style={{ width: column.width }}
                className={cn('px-3 py-3 bg-gray-50', column.className)}
              >
                <div className="space-y-2">
                  <div className="font-semibold text-sm text-gray-900 mb-2">
                    {column.header}
                  </div>
                  {column.customHeaderControl || renderBulkSetControl(column)}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center py-8 text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((column) => (
                  <TableCell
                    key={column.key as string}
                    className={cn('px-2 py-2', column.className)}
                  >
                    {renderCell(row, column, rowIndex)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
        </Table>
      </div>
    </div>
  )
}